import { Spinner } from '@heroui/react';
import { signOut } from 'firebase/auth';
import { MailCheck } from 'lucide-react';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { auth } from '@/config';
import { useHeroToast } from '@/hooks';
import { useUserState } from '@/state';
import { LocaleNavigate, useLocaleNavigate } from '@/utils/locale';

import {
  SubmitButton,
  WelcomeSubtitle,
  WelcomeTitle,
} from '../auth.styled';
import { AuthLayout } from '../components/AuthLayout';

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#14532d' : '#f0fdf4')};
  margin-bottom: 24px;

  svg {
    width: 32px;
    height: 32px;
    color: ${({ theme }) => (theme.mode === 'dark' ? '#4ade80' : '#16a34a')};
  }
`;

const InfoBox = styled.div`
  padding: 16px;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#1e293b' : '#f1f5f9')};
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#334155' : '#dcdfe4')};
  border-radius: 8px;
  margin-bottom: 24px;

  p {
    color: ${({ theme }) => (theme.mode === 'dark' ? '#cbd5e1' : '#475569')};
    font-size: 14px;
    line-height: 1.6;
    margin: 0;
  }
`;

const ResendButton = styled(SubmitButton)`
  background: transparent;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#e5e5e5' : '#374151')};
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#dcdfe4')};

  &:hover:not(:disabled) {
    background: ${({ theme }) => (theme.mode === 'dark' ? '#262626' : '#f9fafb')};
    border-color: ${({ theme }) => (theme.mode === 'dark' ? '#4b5563' : '#d1d5db')};
    box-shadow: none;
    transform: none;
  }
`;

export const VerifyEmail: FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useLocaleNavigate();
  const { showError } = useHeroToast();
  const { currentUser } = useUserState();

  const email = (location.state as { email?: string })?.email;

  // If no email in state, redirect to login
  if (!email) {
    return <LocaleNavigate to="/login" replace />;
  }

  // Poll for email verification status
  useEffect(() => {
    if (!currentUser) return;

    const checkVerification = async () => {
      try {
        await currentUser.reload();
        if (currentUser.emailVerified) {
          navigate('/create-organization');
        }
      } catch (error) {
      }
    };

    // Check immediately
    checkVerification();

    // Then poll every 3 seconds
    const interval = setInterval(checkVerification, 3000);

    return () => clearInterval(interval);
  }, [currentUser, navigate]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      showError('Nie udało się wylogować');
    }
  };

  return (
    <AuthLayout>
      <IconWrapper>
        <MailCheck />
      </IconWrapper>

      <WelcomeTitle>{t('auth.verifyEmail.title')}</WelcomeTitle>
      <WelcomeSubtitle>
        {t('auth.verifyEmail.subtitle', { email })}
      </WelcomeSubtitle>

      <InfoBox>
        <p>{t('auth.verifyEmail.checkSpam')}</p>
      </InfoBox>

      <ResendButton type="button" onClick={handleLogout} style={{ marginTop: '12px' }}>
        Wyloguj
      </ResendButton>
    </AuthLayout>
  );
};
